import { useCallback, useEffect, useRef, useState } from 'react';

import { agentTestEvents } from '@shared/services/agent-test';
import type { EventPayloadByName } from '@shared/services/socket/types';

import { extractRequestId, normalizeMessageText } from '../lib/utils';
import type { StreamMessage, StreamMeta } from '../lib/types';

const STREAM_CHUNK_DURATION = 50;

type UseAgentTestStreamParams = {
  agentId: number;
};

type AgentTestMessagePayload = EventPayloadByName['AGENT_TEST_MESSAGE'];

const resolveNextFullText = (currentText: string, incomingText: string) => {
  if (!incomingText) return currentText;
  if (!currentText) return incomingText;
  if (incomingText.startsWith(currentText)) return incomingText;
  if (currentText.startsWith(incomingText)) return currentText;
  return `${currentText}${incomingText}`;
};

export const useAgentTestStream = ({ agentId }: UseAgentTestStreamParams) => {
  const [streamMessages, setStreamMessages] = useState<StreamMessage[]>([]);
  const pendingRequestIdsRef = useRef<Set<number>>(new Set());
  const finalizedRequestIdsRef = useRef<Set<number>>(new Set());
  const processedMessageIdsRef = useRef<Set<number>>(new Set());
  const streamMetaRef = useRef<Map<number, StreamMeta>>(new Map());
  const streamTimersRef = useRef<Map<number, number>>(new Map());

  const reset = useCallback(() => {
    setStreamMessages([]);
    pendingRequestIdsRef.current.clear();
    finalizedRequestIdsRef.current.clear();
    processedMessageIdsRef.current.clear();
    streamMetaRef.current.clear();
    streamTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    streamTimersRef.current.clear();
  }, []);

  const resetPending = useCallback(() => {
    const finalizedIds = finalizedRequestIdsRef.current;
    setStreamMessages((prev) => prev.filter((msg) => finalizedIds.has(msg.requestId)));
    pendingRequestIdsRef.current.clear();
    streamTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    streamTimersRef.current.clear();
    streamMetaRef.current.clear();
  }, []);

  useEffect(() => {
    reset();
  }, [agentId, reset]);

  const registerRequestId = useCallback((requestId: number) => {
    const normalizedRequestId = Math.abs(requestId);
    pendingRequestIdsRef.current.add(normalizedRequestId);
    finalizedRequestIdsRef.current.delete(normalizedRequestId);

    setStreamMessages((prev) => {
      if (prev.some((msg) => msg.requestId === normalizedRequestId)) return prev;

      return [
        ...prev,
        {
          id: `typing-${normalizedRequestId}`,
          requestId: normalizedRequestId,
          creator_type: 'AI',
          message: '',
          created_at: new Date().toISOString(),
          isStreaming: false,
          isTypingIndicator: true,
        },
      ];
    });
  }, []);

  const ensureStreamMessage = useCallback((requestId: number) => {
    setStreamMessages((prev) => {
      const index = prev.findIndex((msg) => msg.requestId === requestId);
      if (index !== -1) {
        const next = [...prev];
        next[index] = {
          ...next[index],
          id: `stream-${requestId}`,
          message: next[index].message ?? '',
          isTypingIndicator: false,
          isStreaming: true,
        };
        return next;
      }

      return [
        ...prev,
        {
          id: `stream-${requestId}`,
          requestId,
          creator_type: 'AI',
          message: '',
          created_at: new Date().toISOString(),
          isStreaming: true,
          isTypingIndicator: false,
        },
      ];
    });
  }, []);

  const updateStreamMessage = useCallback(
    (requestId: number, updater: (current: StreamMessage) => StreamMessage) => {
      setStreamMessages((prev) => {
        const index = prev.findIndex((msg) => msg.requestId === requestId);
        if (index === -1) return prev;
        const next = [...prev];
        next[index] = updater(next[index]);
        return next;
      });
    },
    [],
  );

  const stopStreamAnimation = useCallback(
    (requestId: number) => {
      const timerId = streamTimersRef.current.get(requestId);
      if (timerId) {
        window.clearTimeout(timerId);
        streamTimersRef.current.delete(requestId);
      }

      const meta = streamMetaRef.current.get(requestId);
      if (!meta) return;

      if (meta.renderedText !== meta.fullText) {
        const finalText = meta.fullText;
        updateStreamMessage(requestId, (current) => ({
          ...current,
          message: finalText,
        }));
        meta.renderedText = finalText;
      }

      streamMetaRef.current.set(requestId, meta);
    },
    [updateStreamMessage],
  );

  const enqueueStreamUpdate = useCallback(
    (requestId: number, rawChunk: string) => {
      const normalizedChunk = normalizeMessageText(rawChunk);
      if (!normalizedChunk) return;

      const meta = streamMetaRef.current.get(requestId) ?? {
        renderedText: '',
        fullText: '',
      };

      const activeTimer = streamTimersRef.current.get(requestId);
      if (activeTimer) {
        window.clearTimeout(activeTimer);
        streamTimersRef.current.delete(requestId);
      }

      if (meta.renderedText !== meta.fullText) {
        const resolvedText = meta.fullText;
        updateStreamMessage(requestId, (current) => ({
          ...current,
          message: resolvedText,
        }));
        meta.renderedText = resolvedText;
      }

      const nextFullText = resolveNextFullText(meta.fullText, normalizedChunk);
      if (nextFullText === meta.fullText) {
        streamMetaRef.current.set(requestId, meta);
        return;
      }

      meta.fullText = nextFullText;
      streamMetaRef.current.set(requestId, meta);

      const timerId = window.setTimeout(() => {
        const latestMeta = streamMetaRef.current.get(requestId);
        if (!latestMeta) {
          streamTimersRef.current.delete(requestId);
          return;
        }

        if (latestMeta.renderedText === latestMeta.fullText) {
          streamTimersRef.current.delete(requestId);
          return;
        }

        const resolvedText = latestMeta.fullText;
        updateStreamMessage(requestId, (current) => ({
          ...current,
          message: resolvedText,
        }));
        latestMeta.renderedText = resolvedText;
        streamMetaRef.current.set(requestId, latestMeta);
        streamTimersRef.current.delete(requestId);
      }, STREAM_CHUNK_DURATION);

      streamTimersRef.current.set(requestId, timerId);
    },
    [updateStreamMessage],
  );

  const resolveRequestIdForFinalMessage = useCallback(() => {
    const pendingRequestIds = [...pendingRequestIdsRef.current];
    return pendingRequestIds[0] ?? null;
  }, []);

  const resolveRequestIdFromPayload = useCallback(
    (payload: AgentTestMessagePayload) => {
      const requestIdFromPayload = Number(payload.message_request_id ?? payload.request_id);
      if (Number.isFinite(requestIdFromPayload)) {
        return Math.abs(requestIdFromPayload);
      }

      const requestIdFromMessage = extractRequestId(payload.message);
      if (requestIdFromMessage) return requestIdFromMessage;

      return resolveRequestIdForFinalMessage();
    },
    [resolveRequestIdForFinalMessage],
  );

  useEffect(() => {
    if (!agentId) return;

    const unsubscribeStream = agentTestEvents.onStream((payload) => {
      const requestId = Math.abs(Number(payload.message_request_id));
      if (!Number.isFinite(requestId)) return;
      if (finalizedRequestIdsRef.current.has(requestId)) return;
      if (!pendingRequestIdsRef.current.has(requestId)) return;

      ensureStreamMessage(requestId);
      enqueueStreamUpdate(requestId, payload.message_part ?? '');
    });

    const unsubscribeMessage = agentTestEvents.onMessage((payload) => {
      const payloadAgentId = Number(payload.agent_id);
      if (Number.isFinite(payloadAgentId) && payloadAgentId !== agentId) return;

      const requestId = resolveRequestIdFromPayload(payload);
      if (!requestId) return;

      if (
        payload.message_id &&
        Number.isFinite(payload.message_id) &&
        processedMessageIdsRef.current.has(payload.message_id)
      ) {
        return;
      }

      if (payload.message_id && Number.isFinite(payload.message_id)) {
        processedMessageIdsRef.current.add(payload.message_id);
      }

      stopStreamAnimation(requestId);

      setStreamMessages((prev) => {
        const index = prev.findIndex((msg) => msg.requestId === requestId);
        const normalizedMessageText = normalizeMessageText(payload.message ?? '');

        if (index === -1) {
          return [
            ...prev,
            {
              id: `stream-${requestId}`,
              requestId,
              creator_type: 'AI',
              message: normalizedMessageText,
              created_at: payload.created_at ?? new Date().toISOString(),
              recommendations: payload.recommendations,
              switched_agent_id: payload.switched_agent_id ?? null,
              finalMessageId: payload.message_id,
              isStreaming: false,
              isTypingIndicator: false,
            },
          ];
        }

        const next = [...prev];
        next[index] = {
          ...next[index],
          message: normalizedMessageText || next[index].message,
          created_at: payload.created_at ?? next[index].created_at,
          recommendations: payload.recommendations ?? next[index].recommendations,
          switched_agent_id:
            payload.switched_agent_id !== undefined
              ? payload.switched_agent_id
              : next[index].switched_agent_id,
          finalMessageId: payload.message_id,
          isStreaming: false,
          isTypingIndicator: false,
        };
        return next;
      });

      pendingRequestIdsRef.current.delete(requestId);
      finalizedRequestIdsRef.current.add(requestId);
      streamTimersRef.current.delete(requestId);
      streamMetaRef.current.delete(requestId);
    });

    return () => {
      unsubscribeStream();
      unsubscribeMessage();
    };
  }, [
    agentId,
    enqueueStreamUpdate,
    ensureStreamMessage,
    resolveRequestIdFromPayload,
    stopStreamAnimation,
  ]);

  useEffect(() => {
    return () => {
      streamTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      streamTimersRef.current.clear();
    };
  }, []);

  return {
    streamMessages,
    registerRequestId,
    reset,
    resetPending,
  };
};
